/**
 * EP-156 T156-007 — the tenant OA's "LINE Official Account" card on the
 * Settings → LINE Integration tab (spec §7 S1). Admin/owner only (the caller
 * gates `canEdit`; the test-connection endpoint itself is admin+).
 *
 * Holds the tenant OA's keys: Channel Secret, Channel Access Token (both
 * write-only — the server never returns the saved value, only a `_present`
 * flag), LIFF ID and the LINE Login Channel ID. The tab's one save bar
 * persists all four through `ref.current.save()`: the card used to have its
 * own Save button beside the page's, and an operator who pressed the page
 * bar (or Enter in a field) got "saved" while the keys were never sent
 * (Peerapat, owner 2026-10-08). "Test connection" (POST /api/line/test_connection) proves the saved
 * token/secret against LINE's own APIs and renders three pass/fail checks;
 * on success the server also caches the OA's display name for the status
 * line above the button.
 *
 * This is NOT the WIN Pickup channel — that card (WinPickupSettingsSection)
 * saves to its own separate settings keys and has no test action.
 */
import { useImperativeHandle, useState, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Check, X } from 'lucide-react'
import { Card, Button, Input, Alert } from '@/components/ui'
import { ManualSetupSteps } from './ManualSetupSteps'
import { api } from '@/lib/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccountSettings, useUpdateAccountSettings, accountSettingsKeys } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'

const API_BASE = import.meta.env.VITE_API_URL || ''

interface LineConnectionTestResult {
  bot: { basic_id: string; display_name: string; picture_url?: string | null }
  webhook: { endpoint: string; active: boolean; signature_ok: boolean }
}

type CheckState = true | false | null // null = not reached by the last attempt

// A2: the four typed failure codes, each naming exactly how far the ladder
// got before it failed — derived here so the checklist reflects the real
// point of failure instead of going all-or-nothing on one error code.
function checksFromErrorCode(code: string | undefined): {
  token: CheckState
  webhook: CheckState
  signature: CheckState
} {
  switch (code) {
    case 'line_token_invalid':
      return { token: false, webhook: null, signature: null }
    case 'webhook_not_flowrent':
      return { token: true, webhook: false, signature: null }
    case 'webhook_signature_failed':
      return { token: true, webhook: true, signature: false }
    default:
      // line_not_configured, or an unrecognized/network failure.
      return { token: null, webhook: null, signature: null }
  }
}

function CheckRow({ state, label }: { state: CheckState; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      {state === true && <Check className="w-4 h-4 text-success shrink-0" aria-hidden="true" />}
      {state === false && <X className="w-4 h-4 text-error shrink-0" aria-hidden="true" />}
      {state === null && <span className="w-4 h-4 shrink-0" aria-hidden="true" />}
      <span className={state === false ? 'text-error' : ''}>{label}</span>
    </div>
  )
}

interface LineOaSettingsCardProps {
  canEdit: boolean
  ref?: Ref<LineOaSettingsCardHandle>
}

export interface LineOaSettingsCardHandle {
  /** Saves whatever was typed into the card. Resolves true when there was nothing to save. */
  save: () => Promise<boolean>
}

export function LineOaSettingsCard({ canEdit, ref }: LineOaSettingsCardProps) {
  const { t } = useTranslation('settings')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('settings')
  const queryClient = useQueryClient()

  const { data: account } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()

  const [liffId, setLiffId] = useState<string | null>(null)
  const [loginChannelId, setLoginChannelId] = useState<string | null>(null)
  const [channelSecret, setChannelSecret] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [urlCopied, setUrlCopied] = useState(false)
  // Local-only: a reminder checklist for the two steps done by hand in LINE
  // Developers. Nothing to persist — there is no account-level field for it.

  const serverLiffId = account?.settings.liff_id ?? ''
  const serverLoginChannelId = account?.settings.line_login_channel_id ?? ''
  const resolvedLiffId = liffId ?? serverLiffId
  const resolvedLoginChannelId = loginChannelId ?? serverLoginChannelId
  const lineOa = account?.settings.line_oa

  const [testResult, setTestResult] = useState<LineConnectionTestResult | null>(null)
  const [testErrorCode, setTestErrorCode] = useState<string | null>(null)
  const [testErrorMessage, setTestErrorMessage] = useState<string | null>(null)

  const testConnection = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ data: LineConnectionTestResult }>('/api/line/test_connection')
      return data.data
    },
    onSuccess: (data) => {
      setTestResult(data)
      setTestErrorCode(null)
      setTestErrorMessage(null)
      // The server caches settings.line_oa on success; re-read it for the status line.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.detail() })
    },
    onError: (err) => {
      setTestResult(null)
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code
      setTestErrorCode(code ?? null)
      const message = translateError(err)
      setTestErrorMessage(message)
      addToast({ type: 'error', message })
    },
  })

  const saveSettings = async (): Promise<boolean> => {
    if (!canEdit) return true
    const dirty = liffId !== null || loginChannelId !== null || channelSecret.trim() !== '' || accessToken.trim() !== ''
    if (!dirty) return true

    const settings: Record<string, string> = {
      liff_id: resolvedLiffId,
      line_login_channel_id: resolvedLoginChannelId,
    }
    if (channelSecret.trim()) settings.line_channel_secret = channelSecret.trim()
    if (accessToken.trim()) settings.line_channel_access_token = accessToken.trim()

    try {
      await updateSettings.mutateAsync({ settings })
      setChannelSecret('')
      setAccessToken('')
      setLiffId(null)
      setLoginChannelId(null)
      return true
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
      return false
    }
  }

  useImperativeHandle(ref, () => ({ save: saveSettings }))

  const handleCopyWebhookUrl = async () => {
    await navigator.clipboard.writeText(`${API_BASE}/webhooks/line`)
    setUrlCopied(true)
    setTimeout(() => setUrlCopied(false), 2000)
  }

  const checks = testResult
    ? { token: true as CheckState, webhook: true as CheckState, signature: true as CheckState }
    : testErrorCode !== null
      ? checksFromErrorCode(testErrorCode)
      : null

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="font-semibold">{t('liff.line_oa.title')}</h3>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm">
          {lineOa?.display_name ? (
            <>
              <span className="text-success">●</span>{' '}
              {t('liff.line_oa.status_connected', { name: lineOa.display_name, basicId: lineOa.basic_id })}
            </>
          ) : (
            <>
              <span className="text-base-content/40">●</span> {t('liff.line_oa.status_not_connected')}
            </>
          )}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          loading={testConnection.isPending}
          onClick={() => testConnection.mutate()}
          data-testid="line-oa-test-connection"
        >
          {t('liff.line_oa.test_connection')}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0">
          <Input
            label={t('liff.line_oa.channel_secret')}
            name="line_channel_secret"
            type="password"
            value={channelSecret}
            onChange={(e) => setChannelSecret(e.target.value)}
            placeholder={
              account?.settings.line_channel_secret_present
                ? t('liff.line_oa.channel_secret_configured')
                : t('liff.line_oa.channel_secret_placeholder')
            }
            disabled={!canEdit}
            autoComplete="off"
          />
        </div>
        <div className="min-w-0">
          <Input
            label={t('liff.line_oa.channel_access_token')}
            name="line_channel_access_token"
            type="password"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            placeholder={
              account?.settings.line_channel_access_token_present
                ? t('liff.line_oa.channel_access_token_configured')
                : t('liff.line_oa.channel_access_token_placeholder')
            }
            disabled={!canEdit}
            autoComplete="off"
          />
        </div>
        <div className="min-w-0">
          <Input
            label={t('liff.liff_id')}
            name="liff_id"
            placeholder={t('liff.liff_id_placeholder')}
            value={resolvedLiffId}
            onChange={(e) => setLiffId(e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="min-w-0">
          <Input
            label={t('liff.line_oa.login_channel_id')}
            name="line_login_channel_id"
            placeholder={t('liff.line_oa.login_channel_id_placeholder')}
            value={resolvedLoginChannelId}
            onChange={(e) => setLoginChannelId(e.target.value)}
            disabled={!canEdit}
          />
          <p className="text-xs text-base-content/60 mt-1">{t('liff.line_oa.login_channel_id_hint')}</p>
        </div>
      </div>

      {checks && (
        <div className="mt-4">
          <p className="text-xs font-medium text-base-content/60 mb-2">{t('liff.line_oa.auto_checks_heading')}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <CheckRow state={checks.token} label={t('liff.line_oa.check_token')} />
            <CheckRow state={checks.webhook} label={t('liff.line_oa.check_webhook')} />
            <CheckRow state={checks.signature} label={t('liff.line_oa.check_signature')} />
          </div>
          {testErrorMessage && (
            <Alert variant="error" className="mt-2">
              {testErrorMessage}
            </Alert>
          )}
        </div>
      )}

      <ManualSetupSteps
        heading={t('liff.line_oa.manual_checks_heading')}
        linkLabel={t('liff.line_oa.how_to')}
        testId="line-oa-manual-steps"
        steps={[
          { text: t('liff.line_oa.manual_step_webhook'), href: 'https://developers.line.biz/en/docs/messaging-api/building-bot/' },
          { text: t('liff.line_oa.manual_step_liff'), href: 'https://developers.line.biz/en/docs/liff/registering-liff-apps/' },
          { text: t('liff.line_oa.manual_step_linked_oa'), href: 'https://developers.line.biz/en/docs/line-login/link-a-bot/' },
        ]}
      />

      {/* Owner 2026-10-08: label above, then the URL and the copy button on
          one centred row of equal height (the label inside Input pushed the
          button off the input's line). */}
      <div className="mt-4">
        <label htmlFor="line-oa-webhook-url" className="label pb-1">
          <span className="label-text">{t('liff.line_oa.webhook_url')}</span>
        </label>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <div className="flex-1 min-w-0">
          <Input
            id="line-oa-webhook-url"
            size="sm"
            value={`${API_BASE}/webhooks/line`}
            readOnly
            aria-readonly="true"
            className="font-mono"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={urlCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          onClick={handleCopyWebhookUrl}
          aria-label={t('liff.line_oa.copy_webhook_url')}
        >
          {urlCopied ? t('liff.line_oa.copied') : t('liff.line_oa.copy')}
        </Button>
        </div>
      </div>
    </Card>
  )
}
